import React, { useState, useEffect, useMemo } from "react";
import { FaCheckCircle, FaExclamationCircle, FaTimes } from "react-icons/fa";

const ProgressiveNotification = ({ resultado, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // 🔹 USAR useMemo para calcular notificaciones de forma estable
  const notificaciones = useMemo(() => {
    const notifs = [];
    
    if (resultado) {
      const creados = resultado.estudiantesCreados || resultado.profesoresCreados || 0;
      
      // ✅ Agregar notificación de éxito si se crearon usuarios
      if (creados > 0) {
        notifs.push({
          tipo: 'success',
          mensaje: `✅ ${creados} usuario(s) creado(s) exitosamente`
        });
      }

      // ⚠️ Agregar errores si existen (verificar que sea array y tenga elementos)
      if (Array.isArray(resultado.errores) && resultado.errores.length > 0) {
        resultado.errores.forEach(error => {
          notifs.push({
            tipo: 'error',
            mensaje: error
          });
        });
      }

      // 📋 Si no se creó nadie y no hay errores explícitos
      if (creados === 0 && (!Array.isArray(resultado.errores) || resultado.errores.length === 0)) {
        notifs.push({
          tipo: 'warning',
          mensaje: '⚠️ No se crearon usuarios. Verifique el archivo Excel.'
        });
      }
    }
    
    return notifs;
  }, [resultado]);

  useEffect(() => {
    if (notificaciones.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      if (!isPaused) {
        setCurrentIndex((prev) => {
          const next = prev + 2;
          if (next >= notificaciones.length) {
            clearInterval(interval);
            setTimeout(() => {
              onClose();
            }, 5000);
            return prev;
          }
          return next;
        });
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [notificaciones, isPaused, onClose]);

  if (notificaciones.length === 0) {
    return null;
  }

  const notificacionesVisibles = notificaciones.slice(0, currentIndex + 2);
  const progress = ((currentIndex + 2) / notificaciones.length) * 100;

  const hayErrores = notificaciones.some(n => n.tipo === 'error');
  const soloExitos = notificaciones.every(n => n.tipo === 'success');

  // Estilos dinámicos
  const borderColor = hayErrores ? 'border-yellow-500' : 'border-green-500';
  const bgHeader = hayErrores ? 'bg-yellow-50' : 'bg-green-50';
  const bgProgress = hayErrores ? 'bg-yellow-500' : 'bg-green-500';
  const iconColor = hayErrores ? 'text-yellow-600' : 'text-green-600';

  return (
    <div 
      className={`fixed top-5 right-5 w-96 bg-white shadow-2xl border-l-4 ${borderColor} rounded-lg overflow-hidden`}
      style={{ zIndex: 9999 }}
    >
      {/* Header */}
      <div className={`${bgHeader} p-3 flex justify-between items-center border-b`}>
        <div className="flex items-center">
          {hayErrores ? (
            <FaExclamationCircle className={iconColor} size={20} />
          ) : (
            <FaCheckCircle className={iconColor} size={20} />
          )}
          <span className="font-bold text-gray-800 ml-2">
            {soloExitos 
              ? '✅ Proceso Exitoso' 
              : hayErrores 
                ? '⚠️ Proceso con Advertencias' 
                : '📋 Resultado'
            }
            <span className="text-sm font-normal ml-2">
              ({Math.min(currentIndex + 2, notificaciones.length)}/{notificaciones.length})
            </span>
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 ml-2 focus:outline-none"
        >
          <FaTimes size={16} />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="w-full h-1 bg-gray-200">
        <div
          className={`h-full ${bgProgress} transition-all duration-300`}
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>

      {/* Content */}
      <div className="max-h-64 overflow-y-auto p-4">
        {notificacionesVisibles.map((notif, index) => (
          <div
            key={index}
            className={`mb-2 p-2 rounded text-sm animate-fadeIn ${
              notif.tipo === 'success' 
                ? 'bg-green-50 border-l-2 border-green-400' 
                : notif.tipo === 'warning'
                ? 'bg-yellow-50 border-l-2 border-yellow-400'
                : 'bg-red-50 border-l-2 border-red-400'
            }`}
          >
            <span className={
              notif.tipo === 'success' 
                ? 'text-green-700' 
                : notif.tipo === 'warning'
                ? 'text-yellow-700'
                : 'text-red-700'
            }>
              {notif.mensaje}
            </span>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="bg-gray-50 p-2 flex justify-between items-center text-xs border-t">
        <button
          onClick={() => setIsPaused(!isPaused)}
          className="text-blue-600 hover:underline focus:outline-none"
        >
          {isPaused ? "▶ Reanudar" : "⏸ Pausar"}
        </button>
        <span className="text-gray-500">
          {currentIndex + 2 < notificaciones.length
            ? "Mostrando más resultados..."
            : "✓ Completado"}
        </span>
      </div>
    </div>
  );
};

export default ProgressiveNotification;